import { useState } from "react";
import { motion } from "framer-motion";
import { MapPin } from "lucide-react";
import { useLang } from "../../i18n/LangContext";
import { useSite } from "../../lib/SiteContext";
import { Reveal, Overline } from "./Reveal";
import { Lightbox } from "./Lightbox";

const STEP = 9;

export const Gallery = () => {
  const { t, lang } = useLang();
  const { gallery } = useSite();
  const [count, setCount] = useState(STEP);
  const [open, setOpen] = useState(null);
  if (!gallery.length) return null;
  const g = t.gallery;

  return (
    <section id="gallery" className="py-24 lg:py-32" data-testid="gallery-section">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <Reveal className="max-w-2xl">
          <Overline>{g.over}</Overline>
          <h2 className="section-title mt-5">{g.title}</h2>
          <p className="mt-4 text-slate-300">{g.sub}</p>
        </Reveal>
        <div className="mt-14 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {gallery.slice(0, count).map((p, i) => {
            const caption = (lang === "hi" && p.caption_hi) || p.caption_en;
            return (
              <motion.figure
                key={p.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.8, delay: (i % 3) * 0.1, ease: [0.22, 1, 0.36, 1] }}
                className="group relative aspect-[4/3] cursor-zoom-in overflow-hidden rounded-[24px] border border-white/10"
                onClick={() => setOpen(i)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === "Enter" && setOpen(i)}
                data-testid={`gallery-item-${i}`}
              >
                <img src={p.image} alt={caption || p.location || "Partner shop"} loading="lazy" className="h-full w-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-ink/90 via-ink/10 to-transparent" />
                <figcaption className="absolute inset-x-0 bottom-0 p-5">
                  {caption && <p className="font-display text-lg font-semibold leading-snug text-white">{caption}</p>}
                  {p.location && <p className="mt-1 flex items-center gap-1.5 text-xs text-slate-200"><MapPin size={12} className="text-saffron" />{p.location}</p>}
                </figcaption>
              </motion.figure>
            );
          })}
        </div>
        {gallery.length > count && (
          <div className="mt-10 text-center">
            <button onClick={() => setCount((c) => c + STEP)} className="btn-ghost" data-testid="gallery-more-btn">{g.more}</button>
          </div>
        )}
      </div>
      {open !== null && <Lightbox items={gallery} index={open} onChange={setOpen} onClose={() => setOpen(null)} labels={g} lang={lang} />}
    </section>
  );
};
